/**
 * Marketing Agents — the specialist catalog (Graphed-parity surface).
 *
 * Four channel categories plus the analytics pack, each specialist a card
 * with mission, cadence, enablement toggle, run-now, and a link into the
 * detail page (charter, runs, artifacts).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  InlineGrid,
  InlineStack,
  Page,
  SkeletonBodyText,
  SkeletonPage,
  Text,
} from '@shopify/polaris';

import {
  listSpecialists,
  runSpecialist,
  setSpecialistEnabled,
} from '../services/marketingSuiteApi';
import type { Specialist } from '../types/marketingSuite';

const CATEGORY_LABELS: Record<string, { title: string; blurb: string }> = {
  paid_ads: {
    title: 'Paid Ads',
    blurb: 'Monitor spend, pace budgets, catch fatigue, propose changes.',
  },
  outbound: {
    title: 'Outbound',
    blurb: 'Lists, sequences, and DM playbooks — sends stay human-gated.',
  },
  seo_content: {
    title: 'SEO & Content',
    blurb: 'Keyword maps and data-grounded drafts from first-party signals.',
  },
  research: {
    title: 'Research',
    blurb: 'Competitor creative tracking and market sizing on live data.',
  },
  analytics: {
    title: 'Analytics Pack',
    blurb: 'The replace-two-analysts layer: reports, anomalies, pacing.',
  },
};

const CATEGORY_ORDER = [
  'paid_ads',
  'outbound',
  'seo_content',
  'research',
  'analytics',
];

function statusBadge(specialist: Specialist) {
  if (!specialist.enabled) return <Badge>Off</Badge>;
  switch (specialist.last_run_status) {
    case 'succeeded':
      return <Badge tone="success">Active</Badge>;
    case 'failed':
      return <Badge tone="critical">Last run failed</Badge>;
    case 'running':
      return <Badge tone="info">Running</Badge>;
    case 'skipped':
      return <Badge tone="attention">Waiting for data</Badge>;
    default:
      return <Badge tone="info">Enabled</Badge>;
  }
}

export default function MarketingAgents() {
  const navigate = useNavigate();
  const [specialists, setSpecialists] = useState<Specialist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await listSpecialists();
      setSpecialists(data.specialists);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load specialists');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const grouped = useMemo(() => {
    const map: Record<string, Specialist[]> = {};
    specialists.forEach((s) => {
      (map[s.category] ||= []).push(s);
    });
    return map;
  }, [specialists]);

  const toggle = async (specialist: Specialist) => {
    setBusyKey(specialist.key);
    try {
      await setSpecialistEnabled(specialist.key, !specialist.enabled);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Toggle failed');
    } finally {
      setBusyKey(null);
    }
  };

  const runNow = async (specialist: Specialist) => {
    setBusyKey(specialist.key);
    try {
      await runSpecialist(specialist.key);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Run failed');
    } finally {
      setBusyKey(null);
    }
  };

  if (loading) {
    return (
      <SkeletonPage title="Marketing Agents">
        <Card>
          <SkeletonBodyText lines={8} />
        </Card>
      </SkeletonPage>
    );
  }

  return (
    <Page
      title="Marketing Agents"
      subtitle="Specialist agents over your warehouse — every artifact grounded, every spend action human-gated."
    >
      <BlockStack gap="500">
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}
        {CATEGORY_ORDER.filter((c) => grouped[c]?.length).map((category) => (
          <BlockStack gap="300" key={category}>
            <BlockStack gap="100">
              <Text as="h2" variant="headingLg">
                {CATEGORY_LABELS[category]?.title ?? category}
              </Text>
              <Text as="p" tone="subdued">
                {CATEGORY_LABELS[category]?.blurb}
              </Text>
            </BlockStack>
            <InlineGrid columns={{ xs: 1, sm: 2, lg: 3 }} gap="300">
              {grouped[category].map((specialist) => (
                <Card key={specialist.key}>
                  <BlockStack gap="200">
                    <InlineStack align="space-between" blockAlign="center">
                      <Text as="h3" variant="headingMd">
                        {specialist.name}
                      </Text>
                      {statusBadge(specialist)}
                    </InlineStack>
                    <Text as="p" tone="subdued">
                      {specialist.mission}
                    </Text>
                    <InlineStack gap="200">
                      <Badge>{specialist.effective_cadence}</Badge>
                      {specialist.requires_sandbox && (
                        <Badge tone="attention">Sandbox</Badge>
                      )}
                      {specialist.recommended_action_types.length > 0 && (
                        <Badge tone="info">Proposes actions</Badge>
                      )}
                    </InlineStack>
                    <InlineStack gap="200">
                      <Button
                        size="slim"
                        loading={busyKey === specialist.key}
                        onClick={() => void toggle(specialist)}
                      >
                        {specialist.enabled ? 'Disable' : 'Enable'}
                      </Button>
                      <Button
                        size="slim"
                        variant="secondary"
                        loading={busyKey === specialist.key}
                        onClick={() => void runNow(specialist)}
                      >
                        Run now
                      </Button>
                      <Button
                        size="slim"
                        variant="plain"
                        onClick={() =>
                          navigate(`/marketing-agents/${specialist.key}`)
                        }
                      >
                        Details
                      </Button>
                    </InlineStack>
                  </BlockStack>
                </Card>
              ))}
            </InlineGrid>
          </BlockStack>
        ))}
        <Box paddingBlockEnd="400" />
      </BlockStack>
    </Page>
  );
}
